import { Component, ComponentInterface, Event, EventEmitter, h, Method, Prop, State, Watch } from "@stencil/core";
import {
    clamp,
    getLocaleSeparators,
    isSingleCharacterEvent,
    isValidNumber,
    parseStringToStringNumber,
    sanitizeNumericInput,
    toMaxPrecision
} from "../../utils/numbers";
import { Cursor } from "../../utils/cursor";
import { Direction, FormatFn, ParseFn } from "./interfaces";
import { splice } from "../../utils/utils";

@Component({
    tag: "uic-input-field-number",
    styleUrl: "input-field-number.scss"
})
export class InputFieldNumber implements ComponentInterface {
    private isTyping = false;
    private didPasteEventJustOccur = false;
    private input: HTMLUicInputFieldElement;
    private inputCursor = new Cursor();

    @State() inputValue = "";

    /**
     * If set to `true`, clamps the value to `min` and `max` after losing focus.
     * The value will be clamped to each bound only if the bound is defined.
     */
    @Prop() readonly clampOnBlur: boolean = false;

    /**
     * The default value of the input after initialization.
     * Also used in combination with `clampOnBlur` to reset to a valid value.
     */
    @Prop() readonly defaultValue?: number;

    /**
     * Set to `true` to disable the input.
     */
    @Prop() readonly disabled: boolean = false;

    /**
     * The label.
     */
    @Prop() readonly label?: string;

    /**
     * The locale to use for input validation.
     */
    @Prop() readonly locale?: string;

    /**
     * Information with low emphasis.
     */
    @Prop() readonly helperText?: string;

    /**
     * If set to `true`, the state is considered invalid.
     */
    @Prop({ mutable: true }) invalid = false;

    /**
     * Information to show if the value is invalid.
     */
    @Prop() readonly invalidText?: string;

    /**
     * The minimum value.
     */
    @Prop() readonly min?: number;

    /**
     * The maximum value.
     */
    @Prop() readonly max?: number;

    /**
     * The placeholder text.
     */
    @Prop() readonly placeholder: string;

    /**
     * The maximum number of decimal places.
     * Set to `0` to only allow integer values.
     */
    @Prop() readonly precision: number = 0;

    /**
     * The increment size to use when using keyboard navigation.
     */
    @Prop() readonly step: number = 1;

    /**
     * A valid number or `Number.NaN`.
     */
    @Prop({ mutable: true }) value: number;

    /**
     * The unit of the value, e.g "mm", "kg".
     */
    @Prop() readonly unit?: string;

    /**
     * An indicator.
     */
    @Prop() readonly flag: string;

    /**
     * Controls if field is marked as requiring some input in case user has
     * not entered anything.
     */
    @Prop() readonly required: boolean = false;

    /**
     * Controls if field is marked as having changed since last save. This is
     * about some visual indicator, only. The actual saving is beyond the scope
     * of this component.
     */
    @Prop() readonly unsaved: boolean = false;

    /**
     * A function that returns a string representation of a given number.
     */
    @Prop() readonly formatter: FormatFn;

    /**
     * A function that parses a number from a given string.
     */
    @Prop() readonly parser: ParseFn;

    /**
     * Emitted when the value changes.
     */
    @Event() uicChange: EventEmitter;

    /**
     * Emitted when a keyboard input occurred.
     */
    @Event() uicInput: EventEmitter;

    /**
     * Emitted when the input is invalid.
     */
    @Event() uicInvalid: EventEmitter;

    @Watch("precision")
    onPrecisionChange() {
        const value = toMaxPrecision(this.value, this.precision);

        if (!this.commitValue(value)) {
            this.onValueChange();
        }
    }

    @Watch("locale")
    @Watch("value")
    @Watch("formatter")
    onValueChange() {
        let formatted: string;

        this.inputCursor.store();

        if (this.formatter) {
            formatted = this.formatter(this.value, this.isTyping);
        }

        if (formatted == null) {
            formatted = this.defaultFormatter();
        }

        this.inputValue = formatted;
    }

    /**
     * Performs a validity check on the input and sets `invalid` accordingly.
     */
    @Method()
    async reportValidity() {
        this.invalid =
            !isValidNumber(this.value) ||
            (this.min != null && this.min > this.value) ||
            (this.max != null && this.max < this.value);

        if (this.invalid) {
            this.uicInvalid.emit();
        }
    }

    /**
     * Sets focus on the native `input` in `uic-input-field-number`.
     * Use this method instead of the global `input.focus()`.
     */
    @Method()
    setFocus(): Promise<void> {
        return this.input.setFocus();
    }

    /**
     * Returns the native `<input>` element used under the hood.
     */
    @Method()
    getInputElement(): Promise<HTMLInputElement> {
        return this.input.getInputElement();
    }

    connectedCallback() {
        this.value = isValidNumber(this.value) ? this.value : this.defaultValue;
        this.onValueChange();
    }

    componentDidLoad() {
        this.input.getInputElement().then((el) => {
            this.inputCursor.input = el;
        });
    }

    componentDidRender() {
        this.inputCursor.restore();
    }

    private defaultFormatter() {
        if (isValidNumber(this.value)) {
            return this.value.toLocaleString(this.locale, {
                maximumFractionDigits: this.precision
            });
        } else {
            return this.inputValue;
        }
    }

    private defaultParser() {
        const numString = parseStringToStringNumber(this.inputValue, this.locale);
        return numString === "" ? Number.NaN : Number(numString);
    }

    private commitValue(value: number): boolean {
        this.isTyping = false;

        if (this.value !== value) {
            this.value = value;
            this.uicChange.emit(this.value);

            return true;
        }

        return false;
    }

    private getStepDelta(direction: Direction) {
        return direction * this.step;
    }

    private incrementValue(delta: number) {
        const currentValue = isValidNumber(this.value) ? this.value : this.defaultValue ? this.defaultValue : 0;
        let nextValue = toMaxPrecision(currentValue + delta, this.precision);
        nextValue = clamp(nextValue, this.min, this.max);
        this.commitValue(nextValue);
    }

    private handleKeydown(event: KeyboardEvent) {
        const { key } = event;
        if (key === "ArrowUp") {
            this.incrementValue(this.getStepDelta(Direction.UP));
        } else if (key === "ArrowDown") {
            this.incrementValue(this.getStepDelta(Direction.DOWN));
        } else if (key === "Enter") {
            this.handleBlur();
        }
    }

    private handleKeypress(event: KeyboardEvent) {
        // allow non-character keys (e.g. CTRL-V)
        if (!isSingleCharacterEvent(event)) {
            return;
        }

        // prevent entering decimals when only integers are allowed
        const { decimalSeparator, thousandsSeparator } = getLocaleSeparators(this.locale);
        if (event.key === decimalSeparator && this.precision === 0) {
            event.preventDefault();
            return;
        }

        // precalculate the new string representation of the number
        const { selectionStart, selectionEnd } = this.inputCursor.input;
        const newStringNumber = splice(this.inputValue, selectionStart, selectionEnd - selectionStart, event.key);

        // if a custom parser is not able to parse the string we prevent the event
        if (this.parser && this.parser(newStringNumber) == null) {
            event.preventDefault();
            return;
        }

        // if the internal parser is not able to parse the string we prevent it as well
        const regex = this.getNumberRegex(decimalSeparator, thousandsSeparator);
        const isNumeric = regex.test(newStringNumber);
        if (!isNumeric || event.key === thousandsSeparator) {
            event.preventDefault();
        }
    }

    private getNumberRegex(decimal: string, thousand: string) {
        const decimalPointsQuantifier = this.precision == null ? "*" : `{0,${this.precision}}`;
        const decimalQuantifier = decimal.replace(".", "\\.");
        const thousandsQuantifier = thousand.replace(".", "\\.");
        return new RegExp(`^[+-]?[\\d${thousandsQuantifier}]*${decimalQuantifier}?\\d${decimalPointsQuantifier}$`);
    }

    private handlePaste() {
        this.didPasteEventJustOccur = true;
    }

    private handleInput(e: Event) {
        e.stopPropagation();

        this.inputValue = this.input.value;
        this.isTyping = true;

        const num = this.parser ? this.parser(this.inputValue) : this.defaultParser();

        this.value = num;
        this.uicInput.emit();
    }

    private handleChange(e: CustomEvent) {
        e.stopPropagation();

        if (this.didPasteEventJustOccur) {
            this.inputValue = sanitizeNumericInput(this.inputValue, this.locale);
            this.didPasteEventJustOccur = false;
        }

        this.uicChange.emit(this.value);
    }

    private handleBlur() {
        if (!this.clampOnBlur) {
            // we may want to format the existing value
            this.isTyping = false;
            this.onValueChange();
            return;
        }

        let nextValue = this.value;

        if (isValidNumber(nextValue)) {
            nextValue = toMaxPrecision(this.value, this.precision);
        } else {
            nextValue = isValidNumber(this.defaultValue) ? this.defaultValue : 0;
        }

        nextValue = clamp(nextValue, this.min, this.max);
        this.commitValue(nextValue);
    }

    render() {
        return (
            <uic-input-field
                ref={(el) => (this.input = el)}
                type='text'
                autocomplete='off'
                inputmode='decimal'
                variant-value
                disabled={this.disabled}
                label={this.label}
                helperText={this.helperText}
                invalid={this.invalid}
                invalidText={this.invalidText}
                placeholder={this.placeholder}
                unit={this.unit}
                value={this.inputValue}
                flag={this.flag}
                required={this.required}
                unsaved={this.unsaved}
                onKeyDown={(e) => this.handleKeydown(e)}
                onKeyPress={(e) => this.handleKeypress(e)}
                onUicChange={(e) => this.handleChange(e)}
                onUicInput={(e) => this.handleInput(e)}
                onPaste={() => this.handlePaste()}
                onUicBlur={() => this.handleBlur()}
            ></uic-input-field>
        );
    }
}
